import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import type { Message } from "@/types/chat.types";

interface GuidedFlowLayoutChatState {
  demoMessages?: Message[];
  demoAction?: { label: string; onClick: () => void };
  demoLoading?: boolean;
}

interface GuidedFlowLayoutChatContextValue extends GuidedFlowLayoutChatState {
  setLayoutDemoChat: (state: GuidedFlowLayoutChatState | null) => void;
}

const GuidedFlowLayoutChatContext = createContext<GuidedFlowLayoutChatContextValue | null>(null);

const noop = () => {};

export function GuidedFlowLayoutChatProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GuidedFlowLayoutChatState | null>(null);

  const setLayoutDemoChat = useCallback((next: GuidedFlowLayoutChatState | null) => {
    setState(next);
  }, []);

  const value = useMemo(
    () => ({
      demoMessages: state?.demoMessages,
      demoAction: state?.demoAction,
      demoLoading: state?.demoLoading,
      setLayoutDemoChat,
    }),
    [state, setLayoutDemoChat],
  );

  return (
    <GuidedFlowLayoutChatContext.Provider value={value}>
      {children}
    </GuidedFlowLayoutChatContext.Provider>
  );
}

export function useGuidedFlowLayoutChat() {
  const ctx = useContext(GuidedFlowLayoutChatContext);
  if (!ctx) {
    return {
      demoMessages: undefined,
      demoAction: undefined,
      demoLoading: undefined,
      setLayoutDemoChat: noop,
    };
  }
  return ctx;
}
